import React from 'react';
import { Box, Skeleton } from '@mui/material';
import { ESG_CONTENT_SHAPE } from '../../constants/colors';
import { useDelayedVisible } from '../LoadingSkeleton';

export interface DashboardSkeletonProps {
  /** `monthly` | `overview` | `branches` | `carbon` */
  variant?: 'monthly' | 'overview' | 'branches' | 'carbon';
  delayMs?: number;
}

const panelSx = {
  borderRadius: `${ESG_CONTENT_SHAPE.md}px`,
  bgcolor: 'background.paper',
  border: 1,
  borderColor: 'divider',
} as const;

/** Section header placeholder: label + description. */
const SectionHead: React.FC<{ width?: number }> = ({ width = 200 }) => (
  <Box sx={{ mb: 2.5 }}>
    <Skeleton variant="text" width={width} height={22} />
    <Skeleton variant="text" width="45%" height={18} />
  </Box>
);

const Hero: React.FC = () => (
  <Box
    sx={{
      ...panelSx,
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', md: '7fr 5fr', lg: '8fr 4fr' },
    }}
  >
    <Box sx={{ p: { xs: 2.5, md: 3.5 } }}>
      <Skeleton variant="text" width={180} height={18} />
      <Skeleton variant="rounded" width="45%" height={52} sx={{ mt: 1.5 }} />
      <Skeleton variant="text" width={220} height={16} sx={{ mt: 1 }} />
    </Box>
    <Box sx={{ p: { xs: 2.5, md: 3.5 }, borderInlineStart: { md: 1 }, borderTop: { xs: 1, md: 0 }, borderColor: 'divider' }}>
      <Skeleton variant="text" width={160} height={18} />
      <Skeleton variant="rounded" width="40%" height={36} sx={{ mt: 1.5 }} />
    </Box>
  </Box>
);

const Strip: React.FC<{ count?: number }> = ({ count = 4 }) => (
  <Box
    sx={{
      ...panelSx,
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: `repeat(${count}, 1fr)` },
    }}
  >
    {Array.from({ length: count }).map((_, i) => (
      <Box key={i} sx={{ p: { xs: 2, md: 2.5 }, borderInlineStart: i ? { md: 1 } : 0, borderColor: 'divider' }}>
        <Skeleton variant="text" width="60%" height={16} />
        <Skeleton variant="rounded" width="50%" height={28} sx={{ mt: 1 }} />
      </Box>
    ))}
  </Box>
);

const TwoUp: React.FC<{ height?: number }> = ({ height = 300 }) => (
  <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', md: '7fr 5fr' } }}>
    <Skeleton variant="rounded" height={height} />
    <Skeleton variant="rounded" height={height} />
  </Box>
);

const Section: React.FC<{ children: React.ReactNode; title?: number }> = ({ children, title }) => (
  <Box sx={{ mb: { xs: 5, md: 6 } }}>
    <SectionHead width={title} />
    {children}
  </Box>
);

/** Loading placeholders that mirror the editorial dashboard layout (no jump on load). */
const DashboardSkeleton: React.FC<DashboardSkeletonProps> = ({ variant = 'monthly', delayMs = 250 }) => {
  const visible = useDelayedVisible(delayMs);
  if (!visible) return null;

  if (variant === 'branches') {
    return (
      <Section title={160}>
        <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' } }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={260} />
          ))}
        </Box>
      </Section>
    );
  }

  if (variant === 'carbon') {
    return (
      <Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 4 }}>
          <Skeleton variant="rounded" width={180} height={40} />
          <Skeleton variant="rounded" width={180} height={40} />
        </Box>
        <Section title={220}>
          <Hero />
          <Box sx={{ mt: 2.5 }}>
            <Strip count={4} />
          </Box>
        </Section>
        <Section title={180}>
          <TwoUp height={320} />
        </Section>
      </Box>
    );
  }

  return (
    <Box>
      <Section title={220}>
        <Hero />
      </Section>
      <Section title={260}>
        <TwoUp height={variant === 'overview' ? 260 : 240} />
      </Section>
      <Section title={180}>
        <Strip count={3} />
      </Section>
      <Section title={140}>
        <Strip count={4} />
      </Section>
    </Box>
  );
};

export default DashboardSkeleton;
